'use es6';

import CodeMirror from 'codemirror';
import FindState from 'react-codemirror/model/FindState';
import FindReplaceMode from 'react-codemirror/enum/FindReplaceMode';
import { JUMP_TO_LINE_REGEX } from 'react-codemirror/constant/Find';
const STATE_CACHE = new WeakMap();
const SCROLL_INTO_VIEW_MARGIN = 20;

function createFindOverlay(cm) {
  const state = getFindState(cm);
  let queryRegex;

  if (state.query instanceof RegExp) {
    queryRegex = state.query;
  } else {
    // Escape string chars for use in RegExp source.
    const pattern = state.query.replace( // eslint-disable-next-line no-useless-escape
    /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    queryRegex = new RegExp(pattern, state.isCaseSensitive ? 'g' : 'gi');
  }

  return {
    token(stream) {
      queryRegex.lastIndex = stream.pos;
      const match = queryRegex.exec(stream.string);

      if (match && match.index === stream.pos) {
        stream.pos += match[0].length || 1;
        return 'searching';
      }

      if (match) {
        stream.pos = match.index;
      } else {
        stream.skipToEnd();
      }

      return null;
    }

  };
}

function getSearchCursor(cm, query, pos) {
  const state = getFindState(cm);
  return cm.getSearchCursor(query, pos, {
    caseFold: !state.isCaseSensitive,
    multiline: true
  });
}

function parseQueryAsRegex(queryString, isCaseSensitive) {
  if (!(queryString && typeof queryString === 'string')) {
    return null;
  }

  try {
    const flags = isCaseSensitive ? 'g' : 'gi';
    return new RegExp(queryString, flags);
  } catch (err) {
    return null;
  }
}

function parseQueryAsString(queryString) {
  if (!(queryString && typeof queryString === 'string')) {
    return null;
  }

  return queryString.replace(/\\(.)/g, (_, ch) => {
    if (ch === 'n') return '\n';
    if (ch === 'r') return '\r';
    if (ch === '\\') return '\\\\';
    return ch;
  });
}

function parseQuery(cm) {
  const state = getFindState(cm);

  if (!state.queryText) {
    state.query = null;
    return;
  }

  state.query = state.isRegex ? parseQueryAsRegex(state.queryText, state.isCaseSensitive) : parseQueryAsString(state.queryText);
}

function addMatchHighlighting(cm) {
  const state = getFindState(cm);

  if (state.overlay) {
    cm.removeOverlay(state.overlay);
  }

  if (state.query) {
    state.overlay = createFindOverlay(cm);
    cm.addOverlay(state.overlay);

    if (cm.showMatchesOnScrollbar) {
      if (state.annotate) {
        state.annotate.clear();
        state.annotate = null;
      }

      state.annotate = cm.showMatchesOnScrollbar(state.query, !state.isCaseSensitive);
      state.matchCount = state.annotate && state.annotate.matches ? state.annotate.matches.length : 0;
    }
  }
}

function removeMatchHighlighting(cm) {
  cm.operation(() => {
    const state = getFindState(cm);

    if (state.overlay) {
      cm.removeOverlay(state.overlay);
      state.overlay = null;
    }

    if (state.annotate) {
      state.annotate.clear();
      state.annotate = null;
    }

    state.matchCount = 0;
  });
}
/**
 * @param  {CodeMirror.Pos}  posA
 * @param  {CodeMirror.Pos}  posB
 * @return {Boolean}
 */


function isCursorPositionsEquivalent(posA, posB) {
  return posA != null && posB != null && posA.line === posB.line && posA.ch === posB.ch;
}

function resetCursorPos(cm) {
  const state = getFindState(cm); // Set both initial cursors to `from` otherwise cursor
  // will jump to next match as input is typed.

  state.posFrom = cm.getCursor('from');
  state.posTo = cm.getCursor('from');
}
/*
 * Get Pos to use with SearchCursor findNext/findPrev.
 */


function getSearchCursorPos(cm, reverse) {
  const state = getFindState(cm); // Check if user moved cursor from state posFrom/posTo

  if (!(isCursorPositionsEquivalent(cm.getCursor('from'), state.posFrom) && isCursorPositionsEquivalent(cm.getCursor('to'), state.posTo))) {
    resetCursorPos(cm);
  }

  return reverse ? state.posFrom : state.posTo;
}

function __findNext(cm, reverse, callback) {
  const state = getFindState(cm);

  if (!state.query) {
    return;
  }

  cm.operation(() => {
    let searchCursor = getSearchCursor(cm, state.query, getSearchCursorPos(cm, reverse));

    if (!searchCursor.find(reverse)) {
      // Attempt searching entire source.
      searchCursor = getSearchCursor(cm, state.query, reverse ? CodeMirror.Pos(cm.lastLine()) : CodeMirror.Pos(cm.firstLine(), 0));

      if (!searchCursor.find(reverse)) {
        return;
      }
    }

    const from = searchCursor.from();
    const to = searchCursor.to();
    cm.setSelection(from, to);
    cm.scrollIntoView({
      from,
      to
    }, SCROLL_INTO_VIEW_MARGIN);
    state.posFrom = from;
    state.posTo = to;

    if (callback) {
      callback(from, to);
    }
  });
}

function __replace(cm, state, searchCursor) {
  if (typeof state.query === 'string') {
    searchCursor.replace(state.replaceText);
  } else if (state.query instanceof RegExp) {
    const match = cm.getRange(searchCursor.from(), searchCursor.to()).match(state.query);
    searchCursor.replace(state.replaceText.replace(/\$(\d)/g, (_, i) => match[i]));
  }
}

function __replaceNext(cm) {
  cm.operation(() => {
    const state = getFindState(cm);

    if (!state.query) {
      return;
    }

    const searchCursor = getSearchCursor(cm, state.query, cm.getCursor('from'));

    if (searchCursor.findNext()) {
      __replace(cm, state, searchCursor);
    }
  });
}

function __replaceAll(cm) {
  cm.operation(() => {
    const state = getFindState(cm);

    if (!state.query) {
      return;
    }

    for (const searchCursor = getSearchCursor(cm, state.query); searchCursor.findNext();) {
      __replace(cm, state, searchCursor);
    }
  });
}

function __initiateFind(cm) {
  removeMatchHighlighting(cm);
  parseQuery(cm);
  addMatchHighlighting(cm);
  resetCursorPos(cm);
}
/**
 * Gets the observable FindState model associated with the
 * passed CodeMirror instance.
 *
 * @param  {CodeMirror} codemirror
 * @return {FindState}
 */


export function getFindState(codemirror) {
  let state = STATE_CACHE.get(codemirror);

  if (state) {
    return state;
  }

  state = new FindState();
  STATE_CACHE.set(codemirror, state);
  return state;
}
/**
 * Adds/updates overlay and annotations with find inputs from state.
 * If find/replace dialog is not shown, it will be displayed..
 *
 * @param  {CodeMirror} cm
 */

export function find(cm) {
  const state = getFindState(cm);

  switch (state.mode) {
    case FindReplaceMode.FIND:
    case FindReplaceMode.REPLACE:
      break;

    default:
      state.mode = FindReplaceMode.FIND;
      break;
  }

  __initiateFind(cm);

  __findNext(cm, false);
}
/**
 * Removes overlay and annotations.
 * Unsets mode.
 *
 * @param  {CodeMirror} cm
 */

export function clear(cm) {
  const state = getFindState(cm);
  state.mode = null;
  removeMatchHighlighting(cm);
}
/**
 * The first match to after the current cursor position will be selected.
 *
 * @param  {CodeMirror} cm
 */

export function findNext(cm) {
  const state = getFindState(cm);

  if (state.mode == null) {
    state.mode = FindReplaceMode.FIND;

    __initiateFind(cm);
  }

  __findNext(cm, false);
}
/**
 * The first match to before the current cursor position will be selected.
 *
 * @param  {CodeMirror} cm
 */

export function findPrev(cm) {
  const state = getFindState(cm);

  if (state.mode == null) {
    state.mode = FindReplaceMode.FIND;

    __initiateFind(cm);
  }

  __findNext(cm, true);
}
/**
 * Selections will be placed on every match.
 *
 * @param  {CodeMirror} cm
 */

export function findAll(cm) {
  const state = getFindState(cm);

  if (state.mode == null) {
    state.mode = FindReplaceMode.FIND;

    __initiateFind(cm);
  }

  if (state.matchCount) {
    const ranges = state.annotate.matches.map(({
      from,
      to
    }) => {
      return {
        anchor: from,
        head: to
      };
    });
    cm.setSelections(ranges);
  }
}
/**
 * The first match to after the current cursor position will be replaced
 * with the value of replaceText.
 *
 * @param  {CodeMirror} cm
 */

export function replace(cm) {
  const state = getFindState(cm);

  if (state.mode == null) {
    __initiateFind(cm);

    __findNext(cm, false);
  }

  if (state.mode !== FindReplaceMode.REPLACE) {
    state.mode = FindReplaceMode.REPLACE;
  }
}
/**
 * The match to be replaced with be the first match
 * after the current primary cursor position.
 *
 * @param  {CodeMirror} cm
 */

export function replaceNext(cm) {
  const state = getFindState(cm);

  if (state.mode !== FindReplaceMode.REPLACE) {
    state.mode = FindReplaceMode.REPLACE;

    __initiateFind(cm);
  }

  __replaceNext(cm);
}
/**
 * All matches will be replaced with the replaceText value.
 *
 * @param  {CodeMirror} cm
 */

export function replaceAll(cm) {
  const state = getFindState(cm);

  if (state.mode !== FindReplaceMode.REPLACE) {
    state.mode = FindReplaceMode.REPLACE;

    __initiateFind(cm);
  }

  __replaceAll(cm);
}
/**
 * Toggles case sensitivity on/off.
 *
 * @param  {CodeMirror} cm
 */

export function toggleCaseSensitivity(cm) {
  const state = getFindState(cm);
  state.isCaseSensitive = !state.isCaseSensitive;
  find(cm);
}
/**
 * Toggles regex mode on/off.
 *
 * @param  {CodeMirror} cm
 */

export function toggleRegex(cm) {
  const state = getFindState(cm);
  state.isRegex = !state.isRegex;
  find(cm);
}
/**
 * Focuses the `.find-input` text box in the FindReplace component.
 *
 * @param {CodeMirror} cm
 */

export function focusFindInput(cm) {
  const {
    panels
  } = cm.state;

  if (!(panels && panels.wrapper)) {
    return;
  }

  const input = panels.wrapper.querySelector('.CodeMirror-hs-findreplace .find-input');

  if (!input) {
    return;
  }

  input.focus();

  if (input.value) {
    input.setSelectionRange(0, input.value.length);
  }
}
/**
 * Opens the Jump To Line form.
 *
 * @param {CodeMirror} cm
 */

export function jumpToLine(cm) {
  const state = getFindState(cm);
  state.mode = FindReplaceMode.JUMP_TO_LINE;
  focusFindInput(cm);
}
/**
 * Sets and scrolls to line or line/column in find state.
 *
 * @param {CodeMirror} cm
 */

export function executeJumpToLine(cm) {
  const state = getFindState(cm);

  if (!state.jumpToLineText) {
    return;
  }

  const [, line, ch] = state.jumpToLineText.match(JUMP_TO_LINE_REGEX);
  const pos = CodeMirror.Pos(Number(line) - 1, ch ? Number(ch) : 0);
  cm.setCursor(pos);
  cm.scrollIntoView(pos, SCROLL_INTO_VIEW_MARGIN);
  cm.focus();
}
/*
 * CodeMirror.commands
 *
 * http://codemirror.net/5/doc/manual.html#commands
 */

/* eslint-disable consistent-return */

CodeMirror.commands.hsFind = cm => {
  const state = getFindState(cm);
  const selections = cm.getSelections();

  if (selections.length === 1 && selections[0]) {
    state.queryText = selections[0];
  }

  state.mode = FindReplaceMode.FIND;
  find(cm);
  focusFindInput(cm);
};

CodeMirror.commands.hsFindNext = findNext;
CodeMirror.commands.hsFindPrev = findPrev;

CodeMirror.commands.hsFindAll = cm => {
  findAll(cm);

  if (getFindState(cm).matchCount) {
    cm.focus();
  }
};

CodeMirror.commands.hsReplace = cm => {
  replace(cm);
  focusFindInput(cm);
};

CodeMirror.commands.hsReplaceNext = replaceNext;
CodeMirror.commands.hsReplaceAll = replaceAll;
CodeMirror.commands.hsToggleFindCase = toggleCaseSensitivity;
CodeMirror.commands.hsToggleFindRegex = toggleRegex;

CodeMirror.commands.hsClearFind = cm => {
  if (getFindState(cm).mode == null) {
    return CodeMirror.Pass;
  }

  clear(cm);
  cm.focus();
};

CodeMirror.commands.hsJumpToLine = jumpToLine;
CodeMirror.commands.hsExecuteJumpToLine = executeJumpToLine;
/* eslint-enable consistent-return */